import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { CalendarIcon, Filter, RotateCcw } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { FilterOptions } from '@/services/admin/trading-accounts-service';

interface TradingAccountsFiltersProps {
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  uniqueFilters: {
    brokers: string[];
    propFirms: string[];
    statuses: string[];
  };
  isLoading?: boolean;
}

export function TradingAccountsFilters({ 
  filters, 
  onFiltersChange, 
  uniqueFilters,
  isLoading 
}: TradingAccountsFiltersProps) {
  const handleFilterChange = (key: keyof FilterOptions, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  const hasActiveFilters = Object.keys(filters).some(key => filters[key as keyof FilterOptions]);

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">Filters</h3>
          </div>
          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearFilters}
              className="h-8 px-2 text-xs"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {/* Search */}
          <div className="space-y-2">
            <Label htmlFor="search" className="text-xs">Search Users</Label>
            <Input
              id="search"
              placeholder="Name, email..."
              value={filters.user_search || ''}
              onChange={(e) => handleFilterChange('user_search', e.target.value)}
              className="h-8"
              disabled={isLoading}
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label className="text-xs">Connection Status</Label>
            <Select 
              value={filters.status || 'all'} 
              onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value)}
              disabled={isLoading}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {uniqueFilters.statuses.map(status => (
                  <SelectItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Refresh Status */}
          <div className="space-y-2">
            <Label className="text-xs">Refresh Status</Label>
            <Select 
              value={filters.refresh_status || 'all'} 
              onValueChange={(value) => handleFilterChange('refresh_status', value === 'all' ? undefined : value)}
              disabled={isLoading}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="All refresh" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All refresh</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Balance Sync Status */}
          <div className="space-y-2">
            <Label className="text-xs">Balance Sync</Label>
            <Select 
              value={filters.balance_sync_status || 'all'} 
              onValueChange={(value) => handleFilterChange('balance_sync_status', value === 'all' ? undefined : value)}
              disabled={isLoading}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="All sync" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sync</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Broker */}
          <div className="space-y-2">
            <Label className="text-xs">Broker</Label>
            <Select 
              value={filters.broker || 'all'} 
              onValueChange={(value) => handleFilterChange('broker', value === 'all' ? undefined : value)}
              disabled={isLoading}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="All brokers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All brokers</SelectItem>
                {uniqueFilters.brokers.map(broker => (
                  <SelectItem key={broker} value={broker}>
                    {broker}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Prop Firm */}
          <div className="space-y-2">
            <Label className="text-xs">Prop Firm</Label>
            <Select 
              value={filters.prop_firm || 'all'} 
              onValueChange={(value) => handleFilterChange('prop_firm', value === 'all' ? undefined : value)}
              disabled={isLoading}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="All firms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All firms</SelectItem>
                {uniqueFilters.propFirms.map(firm => (
                  <SelectItem key={firm} value={firm}>
                    {firm}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
          {/* Live Only */}
          <div className="flex items-center space-x-2">
            <Switch
              id="live-only"
              checked={filters.live_only || false}
              onCheckedChange={(checked) => handleFilterChange('live_only', checked)}
              disabled={isLoading}
            />
            <Label htmlFor="live-only" className="text-xs">Live accounts only</Label>
          </div>

          {/* Date From */}
          <div className="space-y-2">
            <Label className="text-xs">From Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="h-8 justify-start text-left font-normal"
                  disabled={isLoading}
                >
                  <CalendarIcon className="mr-2 h-3 w-3" />
                  {filters.date_from ? format(new Date(filters.date_from), "MMM d, yyyy") : "Start date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.date_from ? new Date(filters.date_from) : undefined}
                  onSelect={(date) => handleFilterChange('date_from', date?.toISOString().split('T')[0])}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Date To */}
          <div className="space-y-2">
            <Label className="text-xs">To Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="h-8 justify-start text-left font-normal"
                  disabled={isLoading}
                >
                  <CalendarIcon className="mr-2 h-3 w-3" />
                  {filters.date_to ? format(new Date(filters.date_to), "MMM d, yyyy") : "End date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.date_to ? new Date(filters.date_to) : undefined}
                  onSelect={(date) => handleFilterChange('date_to', date?.toISOString().split('T')[0])}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};